import classNames from 'classnames';
import React from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import styles from './date-and-prices-table.module.scss';

export interface DateAndPricesTableProps {
  /** The class name to apply */
  className?: string;
  /** The list of columns to show */
  columns: Content.Components['DateAndPricesTableDto']['columns'];
  /** The list of rows to show */
  rows: Content.Components['DateAndPricesTableDto']['rows'];
}

const DateAndPricesTable = ({
  className,
  columns = [],
  rows = [],
}: DateAndPricesTableProps) => {
  return (
    <div className={classNames(styles.component, className)}>
      <table cellSpacing={0} className={styles.table}>
        {columns.length > 0 && (
          <thead className={styles.tableHead}>
            <tr className={styles.tableRow}>
              {columns.map((column, columnIndex) => (
                <th key={columnIndex} className={styles.tableRowItem}>
                  <Typography className={styles.headline} tagStyle="bodySmall">
                    {column.headline}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
        )}

        {rows.length > 0 && (
          <tbody className={styles.tableBody}>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={styles.tableRow}>
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={classNames(styles.tableRowItem, {
                      [styles.isLarge]: columns[cellIndex].size === 'large',
                    })}
                  >
                    <div className={styles.cellContent}>
                      {columns[cellIndex] && (
                        <Typography className={styles.cellContentHeadline}>
                          {columns[cellIndex].headline}
                        </Typography>
                      )}

                      {cell.$type === 'DateAndPricesDateCellDto' ? (
                        <Typography tagStyle="bodySmallBold">
                          {cell.formattedValue}
                        </Typography>
                      ) : cell.$type === 'DateAndPricesPersonCellDto' ? (
                        <div className={styles.cellContentValue}>
                          {cell.image && (
                            <Image
                              alt={cell.image.altText}
                              className={styles.avatar}
                              focalPoint={cell.image.focalPoint}
                              height={20}
                              src={cell.image.url}
                              unoptimized
                              width={20}
                            />
                          )}

                          <Typography tagStyle="bodySmallBold">
                            {cell.value}
                          </Typography>
                        </div>
                      ) : cell.$type === 'DateAndPricesPriceCellDto' ? (
                        <Typography tagStyle="priceSmall">
                          {cell.value}
                        </Typography>
                      ) : (
                        <Typography tagStyle="bodySmallBold">
                          {cell.value}
                        </Typography>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

DateAndPricesTable.displayName = 'DateAndPricesTable';

export default DateAndPricesTable;
